/**
 * Reports Page - Fully Integrated
 * Generate and view various business reports with API integration
 */

import React, { useState } from 'react';
import { Card } from '@components/common/card';
import { Button } from '@components/common/button';
import { Input } from '@components/common/input';
import { toast } from 'react-toastify';
import {
  FileText,
  Download,
  Calendar,
  DollarSign,
  Droplets,
  TrendingUp,
  BarChart3,
  FileSpreadsheet,
} from 'lucide-react';
import reportsService , { ReportParams } from '@/services/reports.service';
// import reportsService, { ReportParams } from '@/services/reports.service';

interface ReportConfig {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  requires_date_range: boolean;
  endpoint: string;
}

export const ReportsPage: React.FC = () => {
  const [start_date, set_start_date] = useState('');
  const [end_date, set_end_date] = useState('');
  const [is_generating, set_is_generating] = useState<string | null>(null);

  const reports: ReportConfig[] = [
    {
      id: 'revenue',
      title: 'Revenue Report',
      description: 'Detailed revenue analysis including invoicing and collection rates',
      icon: <DollarSign size={24} />,
      color: 'bg-green-500',
      requires_date_range: true,
      endpoint: 'revenue'
    },
    {
      id: 'consumption',
      title: 'Consumption Report',
      description: 'Water consumption trends and analysis by client and meter',
      icon: <Droplets size={24} />,
      color: 'bg-blue-500',
      requires_date_range: true,
      endpoint: 'consumption'
    },
    {
      id: 'outstanding',
      title: 'Outstanding Balances',
      description: 'List of all unpaid invoices and overdue accounts',
      icon: <FileText size={24} />,
      color: 'bg-orange-500',
      requires_date_range: false,
      endpoint: 'outstanding'
    },
    {
      id: 'collection',
      title: 'Collection Analysis',
      description: 'Payment collection efficiency and patterns over time',
      icon: <BarChart3 size={24} />,
      color: 'bg-indigo-500',
      requires_date_range: true,
      endpoint: 'collection-analysis'
    },
    {
      id: 'meter_status',
      title: 'Meter Performance',
      description: 'Overview of meter conditions, reading schedules, and alerts',
      icon: <TrendingUp size={24} />,
      color: 'bg-teal-500',
      requires_date_range: false,
      endpoint: 'meter-performance'
    }
  ];

  const validate_dates = (report: ReportConfig): boolean => {
    if (!report.requires_date_range) return true;

    if (!start_date || !end_date) {
      toast.error('Please select start and end dates');
      return false;
    }

    if (new Date(start_date) > new Date(end_date)) {
      toast.error('Start date must be before end date');
      return false;
    }

    return true;
  };

  const handle_generate_report = async (report: ReportConfig, format: 'json' | 'pdf' | 'csv' = 'json') => {
    if (!validate_dates(report)) return;

    try {
      set_is_generating(report.id);
      
      const params: ReportParams = {
        format
      };

      // Add date range if required
      if (report.requires_date_range) {
        params.date_from = start_date;
        params.date_to = end_date;
      }

      toast.info(`Generating ${report.title}...`);

      let response;
      
      // Call appropriate service method based on endpoint
      switch (report.endpoint) {
        case 'consumption':
          response = await reportsService.generateConsumptionReport(params);
          break;
        case 'revenue':
          response = await reportsService.generateRevenueReport(params);
          break;
        case 'outstanding':
          response = await reportsService.generateOutstandingReport(params);
          break;
        case 'collection-analysis':
          response = await reportsService.generateCollectionAnalysis(params);
          break;
        case 'meter-performance':
          // Meter performance only supports json and pdf, convert csv to json
          const meterFormat = format === 'csv' ? 'json' : format;
          response = await reportsService.generateMeterPerformanceReport(meterFormat);
          break;
        default:
          throw new Error('Unknown report type');
      }

      // Handle file download for PDF/CSV
      if (format === 'pdf' || format === 'csv') {
        const filename = `${report.id}_report_${new Date().toISOString().split('T')[0]}.${format}`;
        reportsService.downloadReport(response as Blob, filename);
        toast.success(`${report.title} downloaded successfully`);
      } else {
        // For JSON, could display in a modal or navigate to detail view
        console.log('Report data:', response);
        toast.success(`${report.title} generated successfully`);
      }
      
    } catch (error: any) {
      console.error('Error generating report:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to generate report';
      toast.error(errorMessage);
    } finally {
      set_is_generating(null);
    }
  };

  // const handle_view_report = async (report: ReportConfig) => {
  //   // Generate and view JSON version
  //   await handle_generate_report(report, 'json');
  // };

  const handle_download_pdf = async (report: ReportConfig) => {
    await handle_generate_report(report, 'pdf');
  };

  const handle_download_csv = async (report: ReportConfig) => {
    // CSV only available for some reports
    if (['consumption', 'revenue', 'outstanding'].includes(report.id)) {
      await handle_generate_report(report, 'csv');
    } else {
      toast.info('CSV export not available for this report type');
    }
  };

  const set_quick_date_range = (days: number) => {
    const today = new Date();
    const past = new Date();
    past.setDate(today.getDate() - days);
    
    set_start_date(past.toISOString().split('T')[0]);
    set_end_date(today.toISOString().split('T')[0]);
  };

  const set_month_to_date = () => {
    const today = new Date();
    const start_of_month = new Date(today.getFullYear(), today.getMonth(), 1);
    
    set_start_date(start_of_month.toISOString().split('T')[0]);
    set_end_date(today.toISOString().split('T')[0]);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-charcoal">Reports</h1>
          <p className="text-gray-600 mt-1">
            Generate comprehensive reports for business insights and analysis
          </p>
        </div>
      </div>

      {/* Date Range Filter */}
      <Card title="Date Range" subtitle="Select date range for time-based reports" padding="md">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Start Date"
            type="date"
            value={start_date}
            onChange={(e) => set_start_date(e.target.value)}
            icon_left={<Calendar size={18} />}
          />
          <Input
            label="End Date"
            type="date"
            value={end_date}
            onChange={(e) => set_end_date(e.target.value)}
            icon_left={<Calendar size={18} />}
          />
        </div>

        {/* Quick Date Presets */}
        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => set_quick_date_range(30)}
          >
            Last 30 Days
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => set_quick_date_range(90)}
          >
            Last Quarter
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => set_quick_date_range(365)}
          >
            Last Year
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={set_month_to_date}
          >
            Month to Date
          </Button>
        </div>
      </Card>

      {/* Reports Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {reports.map((report) => (
          <Card key={report.id} padding="md" className="hover:shadow-lg transition-smooth">
            <div className="space-y-4">
              {/* Icon and Title */}
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-lg ${report.color} text-white flex items-center justify-center flex-shrink-0`}>
                  {report.icon}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-charcoal">
                    {report.title}
                  </h3>
                  {report.requires_date_range && (
                    <span className="inline-block mt-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                      Date Range Required
                    </span>
                  )}
                </div>
              </div>

              {/* Description */}
              <p className="text-sm text-gray-600">
                {report.description}
              </p>

              {/* Actions */}
              <div className="pt-3 border-t border-gray-200 space-y-2">
                <Button
                  variant="primary"
                  size="sm"
                  full_width
                  onClick={() => handle_download_pdf(report)}
                  is_loading={is_generating === report.id}
                  icon_left={<Download size={16} />}
                >
                  {is_generating === report.id ? 'Generating...' : 'Download PDF'}
                </Button>
                
                {['consumption', 'revenue', 'outstanding'].includes(report.id) && (
                  <Button
                    variant="outline"
                    size="sm"
                    full_width
                    onClick={() => handle_download_csv(report)}
                    disabled={is_generating === report.id}
                    icon_left={<FileSpreadsheet size={16} />}
                  >
                    Download CSV
                  </Button>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Info Card */}
      <Card padding="md">
        <div className="flex items-start gap-4">
          <FileText size={24} className="text-blue-600 flex-shrink-0 mt-1" />
          <div>
            <h3 className="text-lg font-semibold text-charcoal mb-2">
              About Reports
            </h3>
            <div className="space-y-2 text-sm text-gray-600">
              <p>
                • Reports are generated in PDF and Excel formats for easy sharing and analysis
              </p>
              <p>
                • Time-based reports require a date range selection
              </p>
              <p>
                • All reports include charts, graphs, and detailed breakdowns
              </p>
              <p>
                • Reports are generated in real-time from your current data
              </p>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};